/**
 * Cross-tab de-auth sync
 *
 * When ANY tab removes the auth token — logout, "signed in elsewhere" acknowledge, or
 * the App stale-token cleanup — every OTHER open tab hears the `storage` event and tears
 * itself down too. Without this an idle sibling tab keeps its refresh scheduler + core
 * socket alive (and, post the refresh-path session check, could keep minting tokens) for
 * up to the access-token lifetime.
 *
 * Mirrors the existing cross-tab patterns: `services/theme/theme-sync.ts` and
 * `modules/roleplay-core/hooks/useSessionLock.ts` both listen on the `storage` event.
 */
import { AUTH_TOKEN_KEY } from '@infra/storage';
import { decodeJwtPayload, hasAuthToken, stopTokenRefreshScheduler } from '@infra/auth';
import { disconnectCoreSocket } from '@services/core';
import { setAuthState } from '@services/storage';

let tornDown = false;

/** Decode the `sub` (FusionAuth user id) from a raw stored-tokens JSON string, if any. */
function subFromStoredTokens(raw: string | null): string | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as { access_token?: string };
    return decodeJwtPayload(parsed.access_token)?.sub;
  } catch {
    return undefined;
  }
}

/** Idempotent — a burst of `storage` events (or a later same-key event) must act once. */
function teardown(): void {
  if (tornDown) return;
  tornDown = true;
  stopTokenRefreshScheduler();
  disconnectCoreSocket();
  setAuthState({ isAuthenticated: false, role: null });
  window.location.replace('/');
}

/**
 * Start listening for cross-tab auth-token removal. Returns an unsubscribe fn.
 * Safe to call once at app boot (see `main.tsx`).
 */
export function initCrossTabAuthSync(): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (e: StorageEvent) => {
    // `storage` fires only in OTHER tabs.

    // `localStorage.clear()` (LogoutCallback's wholesale wipe) fires a `key === null`
    // event instead of a key-specific one. Belt-and-braces alongside the key-specific
    // removal `endSession` already does before the FusionAuth hop: if this tab is now
    // tokenless, tear down. Guard against acting on an unrelated `clear()` that left a
    // token in place (shouldn't happen here, but the check is free).
    if (e.key === null) {
      if (!hasAuthToken()) teardown();
      return;
    }

    if (e.key !== AUTH_TOKEN_KEY) return;

    // A null newValue = the token was removed there (logout / session-revoked OK /
    // stale-token cleanup) → we're de-authed.
    if (e.newValue == null) {
      teardown();
      return;
    }

    // Token REPLACED by a DIFFERENT user (a sibling tab logged in as someone else — a
    // replace-mode write, so newValue is non-null). This tab still renders the old
    // identity, but every request now carries the new user's bearer from shared storage.
    // Tear down so it re-auths cleanly as the new user. Same user (refresh rotation or
    // same-account re-login → same `sub`) must NOT trigger this.
    const oldSub = subFromStoredTokens(e.oldValue);
    const newSub = subFromStoredTokens(e.newValue);
    if (oldSub && newSub && oldSub !== newSub) {
      teardown();
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
